import React, { useState } from 'react';
import { DefaultThumbnail } from '@assets';
import AppConfig from '@config';
import { IFile } from '@routes/Common/type';
import useFiles from '@hooks/useFiles';
import Loading from '@components/Loading';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import { RfqDetailData } from '@routes/Common/type';
import { Box, makeStyles, Theme } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import { useStylesCommon } from './useStyles';
import { requestEnumToString } from '@utils/enumToString';
import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationMaterial
} from '@generated/graphql';
import { Download } from '@carbon/icons-react';
import openViewer from '@utils/popupViewer';

interface UseStylesProps {
  matches?: boolean;
}

const useStyles = makeStyles<Theme, UseStylesProps>(() => {
  return {
    boxTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
      '& svg': {
        fontSize: '20px !important'
      }
    },
    displayItemHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 36,
      borderBottom: `1px solid ${colorSet.gray200}`,
      '& :nth-child(1)': {
        width: 'calc(100% - 137px - 73px)'
      },
      '& :nth-child(2)': {
        width: 137,
        padding: (props) => (props.matches ? '0' : '0 8px')
      },
      '& :nth-child(3)': {
        width: 73,
        paddingLeft: 8
      }
    },
    displayItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 0px',
      '& .MuiTypography-colorTextSecondary': {
        color: colorSet.gray600
      },
      borderBottom: `1px solid ${colorSet.gray200}`
    },
    itemIndex: {
      width: 24
    },
    thumbnail: {
      width: 66,
      height: 66,
      border: `1px solid ${colorSet.gray200}`,
      borderRadius: 8,
      cursor: 'pointer'
    },
    fileInfo: {
      display: 'flex',
      flexDirection: (props) => (props.matches ? 'column' : 'row'),
      alignItems: (props) => (props.matches ? 'flex-start' : 'center'),
      width: (props) =>
        props.matches
          ? 'calc(100% - 87px - 66px)'
          : 'calc(100% - 32px - 66px - 73px)',
      paddingLeft: (props) => (props.matches ? 12 : 16),
      '& .MuiSvgIcon-root': {
        cursor: 'pointer',
        color: colorSet.gray700,
        marginLeft: 4
      }
    },
    fileNameWrapper: {
      display: 'flex',
      alignItems: 'center',
      width: (props) => (props.matches ? '100%' : 'calc(100% - 137px)')
    },
    fileName: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: 8,
      maxWidth: 'calc(100% - 30px)',
      '& p': {
        marginRight: 4,
        maxWidth: (props) => (props.matches ? '100%' : 195)
      },
      '& svg': {
        cursor: 'pointer',
        minWidth: 20
      }
    },
    material: {
      width: (props) => (props.matches ? '100%' : 137),
      padding: (props) => (props.matches ? 0 : '0px 8px'),
      wordBreak: 'break-word'
    },
    quantity: {
      width: (props) => (props.matches ? 64 : 80)
    },
    totalQuantity: {
      '& span': {
        color: colorSet.primary500
      },
      marginTop: (props) => (props.matches ? 12 : 16)
    },
    publicClose: {
      width: '100%',
      height: 44,
      borderRadius: 8,
      marginBottom: 16,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colorSet.errorBackGround,
      '& > svg': {
        marginRight: 8
      },
      '& > p': {
        color: colorSet.errorMain
      }
    }
  };
});

interface Props {
  data?: RfqDetailData;
  matches?: boolean;
}

const RfqDrawingFilesPresenter: React.FC<Props> = ({ data, matches }) => {
  const classes = useStyles({ matches: matches });
  const classesCommon = useStylesCommon({ matches: matches });
  const { enqueueSnackbar } = useSnackbar();
  const { onDownloadFile, onDownloadZipFile } = useFiles();
  const [isLoading, setIsLoading] = useState(false);
  const publicOpen = data?.publicOpen;
  const canDownload = true;
  const drawingFiles = data?.drawingFiles ?? [];
  const totalQuantity = drawingFiles.reduce((acc, value) => {
    acc = acc + Number(value.quantity);
    return acc;
  }, 0);

  const onCheckFilePermission = () => {
    if (!publicOpen && !canDownload) {
      enqueueSnackbar('도면 다운로드 권한이 제한된 프로젝트입니다.', {
        variant: 'warning'
      });
      return false;
    }
    return true;
  };

  const onFileDownLoad = (file: IFile) => {
    if (!onCheckFilePermission()) {
      return;
    } else {
      onDownloadFile(file)
        .then((resp) => {
          if (!resp) {
            enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
              variant: 'error'
            });
          }
        })
        .catch(() => {
          enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
            variant: 'error'
          });
        });
    }
  };

  const onFileDownloadAll = () => {
    if (drawingFiles.length < 1) return false;

    const fileArr: Array<IFile> = [];
    setIsLoading(true);

    if (!onCheckFilePermission()) {
      setIsLoading(false);
      return;
    } else {
      drawingFiles.map((file) => {
        if (file.fileId && file.fileName) {
          fileArr.push({ fileId: file.fileId, fileName: file.fileName });
        }
        return null;
      });

      if (fileArr.length > 1) {
        onDownloadZipFile(fileArr)
          .then(() => {
            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
            enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
              variant: 'error'
            });
          });
      } else if (fileArr.length === 1) {
        setIsLoading(false);
        onFileDownLoad(fileArr[0]);
      }
    }
  };

  const ImageThumbnail = ({
    fileId,
    rfqId
  }: {
    fileId: string;
    rfqId?: string;
  }) => {
    const imageUrl = `${AppConfig.hoopsViewerAPI}/${fileId}.png`;
    const thumbnailImg = fileId ? imageUrl : DefaultThumbnail;
    const [error, setError] = useState(false);
    return (
      <img
        onClick={() => {
          openViewer({
            chatRoomId: '',
            rfqId: rfqId,
            selectedFileId: fileId
          });
        }}
        className={classes.thumbnail}
        src={error ? DefaultThumbnail : thumbnailImg}
        alt="iamge"
        onError={() => setError(true)}
      />
    );
  };

  return (
    <React.Fragment>
      <Loading open={isLoading} />
      <Box className={classesCommon.root + ' item'}>
        <div className={classes.boxTitle}>
          <CustomizedTypography
            variant={'h4'}
            color="textPrimary"
            fontWeight="500"
          >
            파트별 제조 수량/재료
          </CustomizedTypography>
          {(publicOpen || canDownload) && drawingFiles.length > 0 && (
            <CustomizedButton
              variant="text"
              size="large"
              startIcon={<Download />}
              onClick={onFileDownloadAll}
            >
              <CustomizedTypography
                variant="body1"
                color="primary"
                fontWeight="500"
              >
                전체 다운로드
              </CustomizedTypography>
            </CustomizedButton>
          )}
        </div>
        {!publicOpen && (
          <div className={classes.publicClose}>
            <Lock color="error" />
            <CustomizedTypography variant="body2" fontWeight="500">
              도면 다운로드를 제한하였습니다
            </CustomizedTypography>
          </div>
        )}
        {drawingFiles.length > 0 ? (
          <>
            <div className={classes.displayItemHeader}>
              <CustomizedTypography variant="subtitle2" color="textSecondary">
                도면
              </CustomizedTypography>
              {!matches && (
                <CustomizedTypography variant="subtitle2" color="textSecondary">
                  재료
                </CustomizedTypography>
              )}

              <CustomizedTypography
                variant="subtitle2"
                color="textSecondary"
                align="right"
              >
                수량
              </CustomizedTypography>
            </div>
            {drawingFiles.map((file, index) => {
              const fileId = file.fileId;
              const fileName = file.fileName;
              const quantity = file.quantity ?? 0;
              const material =
                file.detailedMaterial! ===
                CapaQuotationDetailedMaterialType.Recommend
                  ? requestEnumToString(file.material!)
                  : file.material! === CapaQuotationMaterial.Recommend
                  ? '추천 필요'
                  : requestEnumToString(
                      file.detailedMaterial! || file.material!
                    );

              const recommendDescriptions =
                file.recommendation &&
                `재료 특성: ${file.recommendation
                  .map((recommend) => requestEnumToString(recommend))
                  .join(',')}`;
              return (
                <div key={file.fileId} className={classes.displayItem}>
                  <CustomizedTypography
                    className={classes.itemIndex}
                    variant="body2"
                    color="textSecondary"
                  >
                    {index + 1}
                  </CustomizedTypography>
                  <ImageThumbnail fileId={fileId} rfqId={data?.id} />
                  <div className={classes.fileInfo}>
                    <div className={classes.fileNameWrapper}>
                      <div className={classes.fileName}>
                        <CustomizedTypography
                          variant="body1"
                          color="textPrimary"
                          gutterBottoms={matches ? 4 : 0}
                          noWrap
                        >
                          {fileName}
                        </CustomizedTypography>
                        {(publicOpen || canDownload) && (
                          <Download
                            color={colorSet.primary500}
                            width={20}
                            height={20}
                            onClick={() => {
                              onFileDownLoad(file);
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <CustomizedTypography
                      className={classes.material}
                      variant={'body1'}
                      color={matches ? 'textSecondary' : 'textPrimary'}
                    >
                      {matches ? `재료: ${material}` : material}
                      <CustomizedTypography
                        variant="body1"
                        color="textSecondary"
                      >
                        {recommendDescriptions}
                      </CustomizedTypography>
                    </CustomizedTypography>
                  </div>

                  <CustomizedTypography
                    className={classes.quantity}
                    variant="body1"
                    align="right"
                    fontWeight="500"
                    noWrap
                  >
                    {quantity.toLocaleString('kr')}개
                  </CustomizedTypography>
                </div>
              );
            })}
            <CustomizedTypography
              className={classes.totalQuantity}
              variant="h4"
              fontWeight="500"
              align="right"
            >
              총 수량 <span>{totalQuantity.toLocaleString('kr')}</span>개
            </CustomizedTypography>
          </>
        ) : (
          <CustomizedTypography variant={'body1'} color="textSecondary">
            도면 파일이 없습니다.
          </CustomizedTypography>
        )}
      </Box>
    </React.Fragment>
  );
};

export default RfqDrawingFilesPresenter;
